import type {
  AuditTimelineEntry,
  AuditTrailHistoryTab,
  AuditTrailListItem,
  AuditTrailSummaryStats,
} from '../types/acknowledgementAuditTrail.types';

export function formatAuditTimestamp(value?: string | null): string {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  return date.toLocaleString(undefined, {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatAuditTimeOnly(value?: string | null): string {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

export function formatCriticalityBadge(band?: string | null): { label: string; className: string } {
  const normalized = String(band || '').toLowerCase();
  if (normalized === 'minor') {
    return { label: 'Critical-Minor', className: 'aat-badge aat-badge--minor' };
  }
  return { label: 'Critical-Major', className: 'aat-badge aat-badge--major' };
}

export function resolveDisplayAckStatus(row: AuditTrailListItem): {
  label: string;
  className: string;
} {
  const level = row.currentEscalationLevel ?? 1;
  const ack = String(row.acknowledgementStatus || '');

  if (ack === 'Acknowledged') {
    return { label: 'Acknowledged', className: 'aat-status aat-status--acknowledged' };
  }

  if (ack === 'Cancelled') {
    return { label: 'Pending', className: 'aat-status aat-status--cancelled' };
  }

  if (level > 1) {
    return { label: 'Unacknowledged', className: 'aat-status aat-status--unacknowledged' };
  }

  return { label: 'Pending', className: 'aat-status aat-status--pending' };
}

export function formatAcknowledgementChannel(method?: string | null): string {
  if (!method) {
    return '';
  }
  switch (method.toUpperCase()) {
    case 'SMS':
      return 'SMS';
    case 'IN_APP':
      return 'Mobile App';
    case 'API':
      return 'Web Portal';
    default:
      return method;
  }
}

export function filterTimelineByTab(
  entries: AuditTimelineEntry[],
  tab: AuditTrailHistoryTab
): AuditTimelineEntry[] {
  switch (tab) {
    case 'notificationHistory':
      return entries.filter(entry =>
        ['InitialNotification', 'DeliveryAttempt'].includes(entry.timelineEventType)
      );
    case 'acknowledgementHistory':
      return entries.filter(entry =>
        ['Acknowledgement', 'AdditionalAcknowledgementAttempt'].includes(entry.timelineEventType)
      );
    case 'reminderHistory':
      return entries.filter(entry => entry.timelineEventType === 'ReminderNotification');
    case 'escalationHistory':
      return entries.filter(entry => entry.timelineEventType === 'Escalation');
    case 'deliveryStatus':
      return entries.filter(
        entry =>
          entry.timelineEventType === 'DeliveryAttempt' ||
          Boolean(entry.deliveryStatus) ||
          entry.eventCategory === 'Failed' ||
          entry.eventCategory === 'Delivered'
      );
    default:
      return entries;
  }
}

export function computeSummaryStats(
  rows: AuditTrailListItem[],
  totalRecords: number,
  auditEvents: number
): AuditTrailSummaryStats {
  let acknowledged = 0;
  let pending = 0;
  let unacknowledged = 0;

  rows.forEach(row => {
    const display = resolveDisplayAckStatus(row);
    if (display.label === 'Acknowledged') {
      acknowledged += 1;
    } else if (display.label === 'Unacknowledged') {
      unacknowledged += 1;
    } else if (display.label === 'Pending') {
      pending += 1;
    }
  });

  return {
    totalRecords,
    acknowledged,
    pending,
    unacknowledged,
    auditEvents,
  };
}

export function formatTimelineEventTitle(entry: AuditTimelineEntry): string {
  switch (entry.timelineEventType) {
    case 'InitialNotification':
      return 'Initial Notification';
    case 'DeliveryAttempt':
      return 'Delivery Attempt';
    case 'ReminderNotification':
      return 'Reminder Notification';
    case 'Escalation':
      return 'Escalation';
    case 'Acknowledgement':
      return 'Acknowledgement';
    case 'AdditionalAcknowledgementAttempt':
      return 'Additional Acknowledgement Attempt';
    default:
      return entry.eventType || entry.eventCategory || 'Event';
  }
}

export function formatAcknowledgedByCell(row: AuditTrailListItem): {
  name: string | null;
  meta: string | null;
} {
  const name = row.acknowledgedByName?.trim() || null;
  if (!name) {
    return { name: null, meta: null };
  }

  const channel = formatAcknowledgementChannel(row.acknowledgementMethod);
  const time = row.acknowledgedAt ? formatAuditTimeOnly(row.acknowledgedAt) : '';
  const meta = [channel, time].filter(Boolean).join(' · ');

  return {
    name,
    meta: meta || null,
  };
}

export function resolveAuditTrailStudyLabel(row: AuditTrailListItem): string {
  return row.studyDisplayName?.trim() || row.studyDescription?.trim() || '—';
}

export function buildDetailContextFromListItem(row: AuditTrailListItem): {
  notificationId: number;
  facilityId: number;
  accessionLabel: string;
  studyLabel: string;
} {
  return {
    notificationId: row.id,
    facilityId: row.facilityId,
    accessionLabel: row.accessionNumber || `Notification #${row.id}`,
    studyLabel: resolveAuditTrailStudyLabel(row) === '—' ? '' : resolveAuditTrailStudyLabel(row),
  };
}
